// How a list of listings is paged (listing browse plan, 2026-09-23).
//
// 24 at a time - it divides into one, two, three and four columns without a
// ragged last row. "Show 24 more" adds the next page below; after five of
// those the list stops growing and the page numbers take over, so a phone is
// never asked to hold hundreds of cards. Every page also has its own address,
// ?page=N, which is what a search engine follows.

export const RESULTS_PAGE_SIZE = 24;
export const LOAD_MORE_TIMES = 5;
/** Deepest page served; past it a visitor is better served by a filter. */
export const MAX_RESULTS_PAGE = 1000;

/** One column on a phone, two from 600px, three from 1024px, four from 1440px. */
export const GRID_CLASS =
  'grid grid-cols-1 min-[600px]:grid-cols-2 lg:grid-cols-3 min-[1440px]:grid-cols-4 gap-4 sm:gap-6';

/** The page of every listing in Canada: /en/listings, /fr/annonces. */
/**
 * Where homes for sale are listed and searched: the home page (owner's plan,
 * 2026-09-28) - /en?beds=2, /fr?all=1. /en/listings leads there.
 */
export function listingsPath(lang: string): string {
  return lang === 'fr' ? '/fr' : '/';
}

/** The page of every rental in Canada: /en/rentals, /fr/locations. */
export function rentalsPath(lang: string): string {
  return lang === 'fr' ? '/fr/locations' : '/rentals';
}

/** Where a search of this kind lists its results. */
export const resultsPathFor = (lang: string, deal: 'sale' | 'rent') => (deal === 'rent' ? rentalsPath(lang) : listingsPath(lang));

export function totalPages(total: number, pageSize = RESULTS_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/** A page number from the address, kept inside 1..MAX_RESULTS_PAGE. */
export function pageFrom(raw: unknown): number {
  const n = parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, MAX_RESULTS_PAGE);
}

/** The address of one page of these results: page 1 has no ?page. */
export function pageHref(listPath: string, params: Record<string, unknown>, page: number): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (key === 'page' || value == null || value === '') continue;
    query.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  if (page > 1) query.set('page', String(page));
  const qs = query.toString();
  return qs ? `${listPath}?${qs}` : listPath;
}

/**
 * The numbers the page bar shows: the first, the last, and two either side
 * of the current one, with null for a gap - "1 … 4 5 6 7 8 … 134".
 */
export function pageNumbers(current: number, last: number): Array<number | null> {
  const wanted = new Set([1, last, current - 2, current - 1, current, current + 1, current + 2]);
  const pages = [...wanted].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out: Array<number | null> = [];
  for (const p of pages) {
    const prev = out[out.length - 1];
    if (typeof prev === 'number' && p - prev > 1) out.push(p - prev === 2 ? prev + 1 : null);
    out.push(p);
  }
  return out;
}
